import { z } from 'zod';

/**
 * Permission catalog: the single source of truth for backend and frontend.
 *
 * Scoped permissions are granted with a scope, stored in roles.permissions as
 * "action:scope" (e.g. "records.view:team"):
 *   own  = resources assigned to the member
 *   team = resources assigned to anyone in the member's team (including themself)
 *   all  = the whole tenant
 * Unscoped permissions apply to the whole tenant and are stored as the bare key.
 */
export const SCOPES = ['own', 'team', 'all'] as const;
export type Scope = (typeof SCOPES)[number];

export const PERMISSIONS = {
  'records.view': { label: 'Ver registros', scoped: true },
  'records.edit': { label: 'Editar registros', scoped: true },
  'records.delete': { label: 'Eliminar registros', scoped: true },
  'sensitive.view': { label: 'Ver datos sensibles', scoped: true },
  'publish.web_network': { label: 'Publicar en la web y en la Red', scoped: true },
  'closings.register': { label: 'Registrar cierres', scoped: true },
  'commissions.view': { label: 'Ver comisiones', scoped: true },
  'reports.view': { label: 'Ver reportes', scoped: true },
  'commissions.edit_split': { label: 'Editar reparto de comisiones', scoped: false },
  'payments.register': { label: 'Registrar pagos', scoped: false },
  'payments.approve': { label: 'Aprobar pagos', scoped: false },
  'data.export': { label: 'Exportar datos', scoped: false },
  'data.import': { label: 'Importar datos desde Excel', scoped: false },
  'collaborations.manage': { label: 'Gestionar colaboraciones', scoped: false },
  'settings.manage': { label: 'Configuración de la inmobiliaria', scoped: false },
  'users.manage': { label: 'Gestionar usuarios e invitaciones', scoped: false },
  'roles.manage': { label: 'Gestionar roles y permisos', scoped: false },
  'api_keys.manage': { label: 'Gestionar claves de API', scoped: false },
  'privacy.manage': { label: 'Gestionar privacidad y solicitudes ARCO-POL', scoped: false },
  'users.impersonate': { label: 'Suplantar usuario (con motivo)', scoped: false },
} as const satisfies Record<string, { label: string; scoped: boolean }>;

type Catalog = typeof PERMISSIONS;
export type PermissionKey = keyof Catalog;
export type ScopedPermissionKey = {
  [K in PermissionKey]: Catalog[K]['scoped'] extends true ? K : never;
}[PermissionKey];
export type UnscopedPermissionKey = Exclude<PermissionKey, ScopedPermissionKey>;

/** A grant as stored in roles.permissions. */
export type PermissionGrant = `${ScopedPermissionKey}:${Scope}` | UnscopedPermissionKey;

export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];
export const SCOPED_PERMISSION_KEYS = PERMISSION_KEYS.filter(
  (k) => PERMISSIONS[k].scoped,
) as ScopedPermissionKey[];

export function isPermissionKey(value: string): value is PermissionKey {
  return Object.hasOwn(PERMISSIONS, value);
}

export function isScopedPermission(key: PermissionKey): key is ScopedPermissionKey {
  return PERMISSIONS[key].scoped;
}

export function isScope(value: string): value is Scope {
  return (SCOPES as readonly string[]).includes(value);
}

/** Parses "records.view:team" / "users.manage"; null if it is not a valid grant. */
export function parseGrant(grant: string): { key: PermissionKey; scope: Scope | null } | null {
  const [key, scope, ...rest] = grant.split(':');
  if (key === undefined || rest.length > 0 || !isPermissionKey(key)) return null;
  if (isScopedPermission(key)) {
    return scope !== undefined && isScope(scope) ? { key, scope } : null;
  }
  return scope === undefined ? { key, scope: null } : null;
}

export function isPermissionGrant(value: string): value is PermissionGrant {
  return parseGrant(value) !== null;
}

/** The wider of two scopes (all > team > own). */
export function widestScope(a: Scope, b: Scope): Scope {
  return SCOPES.indexOf(a) >= SCOPES.indexOf(b) ? a : b;
}

/**
 * Deduplicates grants: one entry per permission, keeping the widest scope.
 * Invalid grants are dropped. The result is sorted, so equal sets compare equal.
 */
export function normalizeGrants(grants: readonly string[]): PermissionGrant[] {
  const byKey = new Map<PermissionKey, Scope | null>();
  for (const grant of grants) {
    const parsed = parseGrant(grant);
    if (!parsed) continue;
    const previous = byKey.get(parsed.key);
    byKey.set(
      parsed.key,
      parsed.scope && previous ? widestScope(parsed.scope, previous) : parsed.scope,
    );
  }
  return [...byKey]
    .map(([key, scope]) => (scope ? `${key}:${scope}` : key) as PermissionGrant)
    .sort();
}

export const permissionGrantSchema = z.custom<PermissionGrant>(
  (value) => typeof value === 'string' && isPermissionGrant(value),
  'Permiso no válido',
);

export const BASE_ROLE_KEYS = ['admin', 'manager', 'team_lead', 'agent', 'assistant'] as const;
export type BaseRoleKey = (typeof BASE_ROLE_KEYS)[number];

/** Permissions every tenant must keep on at least one active member (lockout guard). */
export const ADMIN_PERMISSIONS = [
  'users.manage',
  'roles.manage',
] as const satisfies UnscopedPermissionKey[];

const all = (key: ScopedPermissionKey): PermissionGrant => `${key}:all`;

/**
 * Default permissions of the five base roles (PRD matrix). Only Administrador
 * manages settings, users, roles and API keys, and only Administrador impersonates.
 * Tenants can customize roles afterwards.
 */
export const BASE_ROLE_PERMISSIONS: Record<BaseRoleKey, readonly PermissionGrant[]> = {
  admin: normalizeGrants([
    ...SCOPED_PERMISSION_KEYS.map(all),
    ...PERMISSION_KEYS.filter((k) => !isScopedPermission(k)),
  ]),
  manager: normalizeGrants([
    ...SCOPED_PERMISSION_KEYS.map(all),
    'commissions.edit_split',
    'payments.register',
    'payments.approve',
    'data.export',
    'data.import',
    'collaborations.manage',
  ]),
  team_lead: normalizeGrants([
    'records.view:team',
    'records.edit:team',
    'sensitive.view:team',
    'publish.web_network:team',
    'closings.register:team',
    'commissions.view:team',
    'reports.view:team',
  ]),
  agent: normalizeGrants([
    'records.view:own',
    'records.edit:own',
    'sensitive.view:own',
    'publish.web_network:own',
    'closings.register:own',
    'commissions.view:own',
    'reports.view:own',
  ]),
  assistant: normalizeGrants(['records.view:team', 'records.edit:team']),
};
